import { useEffect, useRef } from "react"
import { useBaseMap } from "../BaseMap"
import { JobMetroLayer } from "../../../modules/crm/base/models/JobMetro"
import { JobMetroLayerTypes } from "../../../core/Enums"

interface IProps {
    /** The metro's areas when the map opens. Read once: after that the drawing is the map's. */
    layers?: JobMetroLayer[]
    color?: string
    /** Every change -- a shape drawn, edited or deleted: all the areas as they now are. */
    onChange?: (layers: JobMetroLayer[]) => void
    /** Fit the map to the areas it opened with. */
    fit?: boolean
}

/** One drawn shape as the backend stores it. A rectangle is checked first: it is a polygon too. */
const toMetroLayer = (L: any, layer: any): JobMetroLayer => {
    if (layer instanceof L.Circle)
        return { type: JobMetroLayerTypes.Circle, circleRadius: layer.getRadius(), position: layer.getLatLng(), positions: null }
    if (layer instanceof L.Rectangle)
        return { type: JobMetroLayerTypes.Rectangle, circleRadius: 0, position: null, positions: layer.getLatLngs()[0] }
    if (layer instanceof L.Polygon)
        return { type: JobMetroLayerTypes.Polygon, circleRadius: 0, position: null, positions: layer.getLatLngs()[0] }
    return { type: JobMetroLayerTypes.None, circleRadius: 0, position: null, positions: null }
}

/**
 * Drawing a metro's areas: circles, rectangles and polygons, with Leaflet.draw's toolbar (draw,
 * edit, delete) at the map's top right. Saved the same way as with the old map.
 */
export default function MetroDrawLayer(props: IProps) {

    const { L, map } = useBaseMap()
    const onChange = useRef(props.onChange)
    onChange.current = props.onChange
    const initial = useRef(props.layers)

    useEffect(() => {
        const color = props.color || "#1b84ff"
        const shapeOptions = { color, weight: 3, fillColor: color, fillOpacity: .15 }

        const drawn = new L.FeatureGroup().addTo(map)
        initial.current?.forEach(layer => {
            let shape: any
            if (layer.type === JobMetroLayerTypes.Circle && layer.position) shape = L.circle(layer.position, { ...shapeOptions, radius: layer.circleRadius })
            else if (layer.type === JobMetroLayerTypes.Rectangle && layer.positions) shape = L.rectangle(layer.positions, shapeOptions)
            else if (layer.type === JobMetroLayerTypes.Polygon && layer.positions) shape = L.polygon(layer.positions, shapeOptions)
            if (shape) drawn.addLayer(shape)
        })
        if (props.fit && drawn.getLayers().length) map.fitBounds(drawn.getBounds(), { padding: [30, 30] })

        const noCrossing = { color: "#e1e100", message: "<strong>Lines of an area cannot cross each other.</strong>" }
        const control = new L.Control.Draw({
            position: "topright",
            draw: {
                marker: false,
                polyline: false,
                circlemarker: false,
                circle: { shapeOptions },
                rectangle: { shapeOptions },
                polygon: { allowIntersection: false, drawError: noCrossing, shapeOptions }
            },
            edit: { featureGroup: drawn, remove: true }
        })
        map.addControl(control)

        const changed = () => onChange.current?.(drawn.getLayers().map((l: any) => toMetroLayer(L, l)))
        const created = (e: any) => {
            drawn.addLayer(e.layer)
            changed()
        }
        map.on(L.Draw.Event.CREATED, created)
        map.on(L.Draw.Event.EDITED, changed)
        map.on(L.Draw.Event.DELETED, changed)

        return () => {
            map.off(L.Draw.Event.CREATED, created)
            map.off(L.Draw.Event.EDITED, changed)
            map.off(L.Draw.Event.DELETED, changed)
            map.removeControl(control)
            drawn.remove()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map])

    return null
}
